'use client';

import { MouseEvent, useContext, useEffect, useState } from 'react';
import Accordion from 'components/library/Accordion/accordion';
import UpsellItem from 'components/pdp/upsell-item';
import PDPPrice, {
  ProductIntroPricing,
  getProductPricing,
} from 'components/pdp/product-price';
import OtpOfferSelector from 'components/pdp/OtpOfferSelector';
import ProductBundleInfo from 'components/pdp/product-bundle-info';
import { CartContext } from 'context-providers/cart-provider';
import { discountAppliesToProduct } from 'lib/utils/coupons/discount-applies-to-product';
import type { ProductPromoCode } from 'lib/utils/coupons/resolve-promo-code';
import { useOtpBuybox } from 'components/pdp/OtpOfferSelector/use-otp-buybox';
import { trackButtonEvent, trackEvent } from 'lib/analytics';
import PdpLocale from 'locale/en/pdp.json';
import styled, { css } from 'styled-components';
import { BodySmReg, BodyMedReg, BodyXxsReg } from '@seed-health/tokens';
import { Srp0Title } from 'components/ds01/hero-styled';
import { mqLargeMin } from 'styled/tools/media-queries';
import Button from 'components/global/Button/Primary';
import Rating from 'components/global/Rating';
import { useActiveMiniSale } from 'hooks/use-active-mini-sale';
import { computeMiniSalePrice } from 'lib/mini-sale/mini-sale';
import { getLocalizedPrice } from 'lib/utils/get-localized-price';
import { ProductsContext } from 'context-providers/products-provider';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';
import { SHOPIFY_FUNCTION_BOGO_CODES } from 'lib/constants/shopify-function-codes';

const RatingContainer = styled.div`
  display: flex;
  align-items: center;

  @media ${mqLargeMin} {
    padding-top: 16px;
  }
  padding-bottom: 16px;

  img {
    margin-right: 1rem;
    border-right: 1px solid var(--color-neutral-faded-green-20);
    padding-right: 1rem;
    max-height: 35px;
  }
`;

const GuaranteeContainer = styled.div`
  margin: 1rem 0 1.5rem;
  text-align: center;
  font-size: 14px;
  opacity: 0.5;

  p + & {
    margin-top: var(--spacing-4);
  }
`;

const ProductTitle = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;

  h1 {
    font-size: 24px;
    font-weight: 350;
    line-height: 110%;
    letter-spacing: -0.64px;
  }

  @media ${mqLargeMin} {
    font-size: 32px;
  }
`;

const Srp0TitlePDP = styled(Srp0Title)`
  padding-left: 0;
  padding-right: 0;
`;

const TitlePill = styled.div<{ $isSmall?: boolean }>`
  display: flex;
  height: 27px;
  padding: 8px 8px 6px;
  justify-content: center;
  align-items: center;
  border-radius: 50px;
  border: 1.75px solid var(--fill-brand);

  ${({ $isSmall }) =>
    $isSmall
      ? css`
          ${BodySmReg};
          padding-bottom: 8px;
        `
      : css`
          ${BodyMedReg};
        `}
`;

const PriceWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AddToCartWrapper = styled.div`
  margin-top: var(--spacing-24);

  a {
    width: 100%;
  }
`;

const AccordionWrapper = styled.div`
  ul {
    margin-top: 10px;
    margin-left: 1rem;
    padding-left: 10px;
    list-style-type: disc;
    list-style-position: outside;

    li {
      font-size: 16px;
      font-weight: 350;
      line-height: 140%;
      letter-spacing: -0.16px;

      &::marker {
        margin-right: 10px !important;
      }
    }

    li + li {
      margin-top: 10px;
    }
  }
`;

const Citations = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
`;

const CitationLine = styled.p`
  ${BodyXxsReg};
  color: var(--fill-brand-t50);
`;

const ProductPageInformation = ({
  accordionItems,
  selectedProduct,
  selectedSku,
  productInfo,
  bundleProduct,
  bundleProducts,
  yotpoAverageScore,
  yotpoTotalReviews,
  ds01SalePrice,
  guaranteeHtml,
  citations,
}) => {
  // Consume promo code discount info from cart context
  // The cart provider already handles fetching, comparing, and persisting the winning code
  const { promoCodeDiscountInfo, openCart } = useContext(CartContext);

  const [promoCode, setPromoCode] = useState<ProductPromoCode | false>(false);

  const clientMiniSale = useActiveMiniSale();
  const { currencyCode } = useContext(ProductsContext);

  // Null before mount to avoid hydration mismatch; falls back to SSR prop.
  const miniSalePrice =
    clientMiniSale === null
      ? (ds01SalePrice ?? null)
      : clientMiniSale.ds01SalePercent == null
        ? null
        : currencyCode === 'USD'
          ? (ds01SalePrice ??
            computeMiniSalePrice(
              selectedProduct?.price,
              clientMiniSale.ds01SalePercent,
            ))
          : computeMiniSalePrice(
              getLocalizedPrice(selectedProduct),
              clientMiniSale.ds01SalePercent,
            );

  useEffect(() => {
    if (!promoCodeDiscountInfo) {
      setPromoCode(false);
      return;
    }

    // Free-gift app codes only discount the monthly DS-01, never a bundle.
    const freeGiftPercent =
      selectedSku === PRODUCT_SKUS.DAILY_SYNBIOTIC && !bundleProduct
        ? SHOPIFY_FUNCTION_BOGO_CODES[promoCodeDiscountInfo.code.toUpperCase()]
        : undefined;

    if (discountAppliesToProduct(productInfo.id, promoCodeDiscountInfo.items)) {
      setPromoCode({
        code: promoCodeDiscountInfo.code,
        value: promoCodeDiscountInfo.value,
        recurringCycleLimit: promoCodeDiscountInfo.recurringCycleLimit,
      });
    } else if (freeGiftPercent) {
      setPromoCode({
        code: promoCodeDiscountInfo.code,
        value: { percentage: freeGiftPercent },
      });
    } else {
      setPromoCode(false);
    }
  }, [promoCodeDiscountInfo, productInfo.id, selectedSku, bundleProduct]);

  const handleClickCta = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    trackButtonEvent('Start Now', window.location.pathname, {});
    openCart({ skus: cartQueryParam.split(',') });
  };

  const ds01MiniSalePrice =
    selectedSku === PRODUCT_SKUS.DAILY_SYNBIOTIC ? miniSalePrice : null;
  const otpBuybox = useOtpBuybox(currencyCode);
  const {
    price: firstOrderPrice,
    bundledPrice: regularPrice,
    discountTag,
  } = getProductPricing({
    selectedProduct,
    bundleProduct,
    promoCode,
    salePriceOverride: ds01MiniSalePrice,
    currencyCode,
  });

  const handleClickReviews = () => {
    trackEvent('Button Clicked', {
      buttonName: 'More Reviews',
      location: 'PDP',
    });
  };

  const cartQueryParam =
    productInfo.productType === 'Bundle' && bundleProduct
      ? `${selectedProduct.productSku},${bundleProduct.productSku}`
      : selectedProduct.productSku;

  return (
    <>
      <Srp0TitlePDP className='srp0title' data-testid='ds01-srp0title'>
        <div>
          <ProductTitle>
            {productInfo.shortTitle &&
              productInfo.shortTitle !== productInfo.title && (
                <TitlePill
                  $isSmall={selectedSku === PRODUCT_SKUS.DAILY_SYNBIOTIC}
                >
                  {productInfo.shortTitle}
                </TitlePill>
              )}
            <h1>
              {productInfo.shortTitle &&
              productInfo.shortTitle !== productInfo.title
                ? productInfo.title.replace(productInfo.shortTitle, '')
                : productInfo.title}
            </h1>
          </ProductTitle>
        </div>
      </Srp0TitlePDP>
      {yotpoAverageScore != null && yotpoTotalReviews != null && (
        <RatingContainer>
          <Rating
            value={yotpoAverageScore}
            reviewCount={yotpoTotalReviews}
            reviewsHref='#reviews-section'
            onClick={handleClickReviews}
          />
        </RatingContainer>
      )}
      <p>{productInfo?.description}</p>
      {otpBuybox.isActive && selectedSku === PRODUCT_SKUS.DAILY_SYNBIOTIC ? (
        <OtpOfferSelector
          firstOrderPrice={firstOrderPrice}
          regularPrice={regularPrice}
          oneTimePrice={otpBuybox.oneTimePrice}
          promoCode={(discountTag && promoCode) || null}
          subscriptionHref={`/cart?cart=${cartQueryParam}`}
          onStartSubscription={handleClickCta}
        />
      ) : (
        <>
          <PriceWrapper>
            <PDPPrice
              promoCode={promoCode}
              guaranteeHtml={guaranteeHtml}
              selectedProduct={selectedProduct}
              bundleProduct={bundleProduct}
              salePriceOverride={ds01MiniSalePrice}
              {...(productInfo.tags[0] ? { tag: productInfo.tags[0] } : false)}
            />
          </PriceWrapper>
          <AddToCartWrapper>
            <Button
              href={`/cart?cart=${cartQueryParam}`}
              onClick={handleClickCta}
              size='md'
            >
              Start Now
            </Button>
          </AddToCartWrapper>
          <ProductIntroPricing
            promoCode={promoCode}
            selectedProduct={selectedProduct}
            bundleProduct={bundleProduct}
            salePriceOverride={ds01MiniSalePrice}
          />
          <GuaranteeContainer tabindex='0'>
            {PdpLocale.pdp_guarantee}
          </GuaranteeContainer>
        </>
      )}
      {productInfo.productType !== 'Bundle' && (
        <AccordionWrapper>
          <Accordion items={accordionItems} displayContentAsHTML={true} />
        </AccordionWrapper>
      )}
      {productInfo.productType === 'Bundle' && (
        <ProductBundleInfo
          selectedProduct={selectedProduct}
          bundleProduct={bundleProduct}
          bundleProducts={bundleProducts}
        />
      )}
      {selectedProduct && <UpsellItem product={selectedProduct} />}
      <Citations>
        {(citations ?? []).map((citation, index) => (
          <CitationLine key={index}>{citation}</CitationLine>
        ))}
      </Citations>
    </>
  );
};

export default ProductPageInformation;
